import React, { useState } from 'react';

import { Alert, Box, Stack } from '@mui/material';

import AddBusinessSettingForm from '~/components/LeBusinessTraveller/components/businessSettings/AddBusinessSettingForm';
import SocieteSettingForm, {
  SocieteSettingDraft,
} from '~/components/LeBusinessTraveller/components/businessSettings/SocieteSettingForm';

import { BusinessSettingName, BusinessSettingsResponse } from '~/services/BusinessTraveller/BusinessTravellerService';

import { DEFAULT_SOCIETE, SETTING_NAMES } from '~/utils/businessTraveller/businessSettings';
import featureToggle from '~/utils/featureToggle';

import SabreSyncSettingForm, { SabreSyncSettingDraft } from './SabreSyncSettingForm';

// Tab-local state mirrors the server BusinessSettingsResponse shape but each entry's id is optional
// (drafts the user just added via "Add setting" have no id until first save).
type BusinessSettingForms = {
  societe?: SocieteSettingDraft;
  sabre_sync?: SabreSyncSettingDraft;
};

interface Props {
  businessId?: string;
  initialSettings: BusinessSettingsResponse;
  error: Error | null;
}

export default function BusinessSettingTabContent({ businessId, initialSettings, error }: Props) {
  const [businessSettingForms, setBusinessSettingForms] = useState<BusinessSettingForms>(initialSettings);

  const societeForm = businessSettingForms.societe;
  const sabreSyncForm = businessSettingForms.sabre_sync;
  const isSabreSyncAvailable = !!businessId && featureToggle.availableToShow('LEBT_SABRE_PROFILES_ENABLED');
  const settingNames = SETTING_NAMES.filter((name) => name !== 'sabre_sync' || isSabreSyncAvailable);
  const existingSettingNames = Object.keys(businessSettingForms) as Array<BusinessSettingName>;
  const isEmpty = existingSettingNames.length === 0;

  const handleAdd = (settingName: BusinessSettingName) => {
    if (settingName === 'societe') {
      setBusinessSettingForms((prev) => ({ ...prev, societe: { value: DEFAULT_SOCIETE } }));
    } else if (settingName === 'sabre_sync' && isSabreSyncAvailable) {
      setBusinessSettingForms((prev) => ({ ...prev, sabre_sync: { value: { enabled: false } } }));
    }
  };

  return (
    <Box>
      <Stack gap={3}>
        {error && <Alert severity="error">Error fetching business settings</Alert>}

        {isEmpty && (
          <Alert severity="info">
            {businessId
              ? 'No settings for this business. Add a setting to configure one.'
              : 'No default settings configured. Add a setting to set a default.'}
          </Alert>
        )}

        {societeForm && <SocieteSettingForm setting={societeForm} businessId={businessId} />}

        {businessId && isSabreSyncAvailable && sabreSyncForm && (
          <SabreSyncSettingForm businessId={businessId} setting={sabreSyncForm} disabled={!!error} />
        )}

        <AddBusinessSettingForm
          settingNames={settingNames}
          existingSettingNames={existingSettingNames}
          onAdd={handleAdd}
        />
      </Stack>
    </Box>
  );
}
